import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseGitGuidelinesSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage git guidelines versions
 * Stores and tracks different versions of git guidelines from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useGitGuidelinesVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for git guidelines version management
  const gitGuidelinesConfig = {
    // Determine if a message contains git guidelines content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // More specific matching to avoid false positives
      const hasGitGuidelinesKeywords =
             content.includes('Git Guidelines') ||
             content.includes('Branching Strategy') ||
             content.includes('Branch Naming') ||
             content.includes('Commit Convention') ||
             content.includes('Commit Message') ||
             content.includes('Git Workflow') ||
             content.includes('Git Flow') ||
             content.includes('Merge Strategy') ||
             content.includes('Pull Request') ||
             content.includes('Code Review') ||
             content.includes('git checkout') ||
             content.includes('git branch') ||
             content.includes('git commit') ||
             content.includes('git merge') ||
             content.includes('git rebase');
      // Must have git guidelines keywords, not just any markdown headers
      return hasGitGuidelinesKeywords;
    },

    // Parse git guidelines sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseGitGuidelinesSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(gitGuidelinesConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to git guidelines-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useGitGuidelinesVersions;
